/**
 * Aug-2026 (User-Wunsch) — Lern-Übersicht: gelernte Scan-Korrekturen der KI
 * einsehen und einzeln löschen (z. B. wenn eine Korrektur falsch gelernt wurde).
 */
import { useEffect, useState } from "react";
import { Brain, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const FIELD_LABELS = {
    previous_owner_first_name: "Vorname", previous_owner_last_name: "Nachname",
    previous_owner_street: "Straße", previous_owner_zip: "PLZ", previous_owner_city: "Ort",
    vin: "FIN", brand: "Marke", model: "Modell", license_plate: "Kennzeichen",
    hsn: "HSN", tsn: "TSN", first_registration: "Erstzulassung", first_registration_date: "Erstzulassung",
    mileage: "KM-Stand", color: "Farbe", work_description: "Auftragstext",
    customer_phone: "Telefon", customer_email: "E-Mail", order_number: "Beleg-/Auftragsnr.",
};
const fieldLabel = (k) => FIELD_LABELS[k] || k;
const DOC_LABELS = { zb1: "ZB1", coc: "COC", note: "Notiz", unknown: "Sonstiges" };

export default function ScanCorrectionsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState("");

    const load = () => {
        api.get("/scan-corrections")
            .then((r) => setItems(r.data?.items || []))
            .catch(() => toast.error("Korrekturen konnten nicht geladen werden"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const remove = async (c) => {
        setDeleting(c.id);
        try {
            await api.delete(`/scan-corrections/${c.id}`);
            setItems((prev) => prev.filter((x) => x.id !== c.id));
            toast.success("Korrektur gelöscht — wird bei künftigen Scans nicht mehr angewendet");
        } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
        finally { setDeleting(""); }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-8" data-testid="scan-corrections-page">
            <div className="flex items-center gap-3 mb-1">
                <Brain className="w-7 h-7 text-[#FFCC00]" />
                <h1 className="text-2xl font-black text-[#111827]">Scan-Lernfunktion — gelernte Korrekturen</h1>
            </div>
            <p className="text-sm text-gray-500 mb-6">
                Manuelle Korrekturen nach KI-Scans werden hier gesammelt. Die KI nutzt sie als Hinweise
                und ersetzt exakt wiederkehrende Fehler automatisch. Falsch Gelerntes kannst du hier löschen.
            </p>
            {loading ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
            ) : items.length === 0 ? (
                <p className="text-sm text-gray-400 border border-dashed border-gray-300 rounded-sm px-4 py-6 text-center" data-testid="scan-corrections-empty">
                    Noch keine gelernten Korrekturen — korrigiere Felder direkt im Scan-Dialog, das System merkt sie sich automatisch.
                </p>
            ) : (
                <div className="border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 bg-gray-50">
                                <th className="px-3 py-2">Feld</th>
                                <th className="px-3 py-2">Falsch erkannt</th>
                                <th className="px-3 py-2">Korrekt</th>
                                <th className="px-3 py-2">Dokument</th>
                                <th className="px-3 py-2 text-right">Gelernt</th>
                                <th className="px-3 py-2 text-right">Zuletzt</th>
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c, i) => (
                                <tr key={c.id} className="border-t border-gray-100" data-testid={`scan-correction-row-${i}`}>
                                    <td className="px-3 py-2 font-bold text-[#111827]">{fieldLabel(c.field)}</td>
                                    <td className="px-3 py-2"><span className="line-through text-red-600 font-mono-num">{c.wrong_value}</span></td>
                                    <td className="px-3 py-2 font-bold text-emerald-700 font-mono-num">{c.correct_value}</td>
                                    <td className="px-3 py-2 text-gray-500">{DOC_LABELS[c.document_type] || c.document_type}</td>
                                    <td className="px-3 py-2 text-right font-mono-num">{c.count}×</td>
                                    <td className="px-3 py-2 text-right text-gray-400 text-xs font-mono-num">{(c.updated_at || "").slice(0, 10)}</td>
                                    <td className="px-3 py-2 text-right">
                                        <button type="button" disabled={deleting === c.id} onClick={() => remove(c)}
                                            className="text-red-500 hover:text-red-700 disabled:opacity-50" title="Korrektur löschen"
                                            data-testid={`scan-correction-delete-${i}`}>
                                            {deleting === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
